import axios from 'axios';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { setAreTranslationsLoading } from './containers/App/AppSlice';
import { store } from './store';
import { getItem, setItem } from './utils/storage';
import { getSystemLanguage } from './utils/getSystemLanguage';
import { EnvironmentEnum } from './config/region';

export enum ELanguages {
  'se' = 'se',
  'fi' = 'fi',
  'pt' = 'pt',
  'nl' = 'nl',
  'es' = 'es',
  'en' = 'en',
  'en-TT' = 'en-TT',
}

export const originalLanguageNames = {
  se: 'Svenska',
  fi: 'Suomi',
  nl: 'Nederlands',
  pt: 'Português',
  es: 'Español',
  'en-TT': 'English',
  en: 'English',
} as const;

export const allowedLanguages = Object.values(ELanguages);

export const fallbackLanguage = ELanguages.en;

export const getDefaultLanguage = () => fallbackLanguage;

export const getAppLanguage = () => getItem('language') || fallbackLanguage;

const initAppLanguage = async (): Promise<string> => {
  migrateSavedLegacyEnTTLanguage();
  const storedLanguage = getItem('language');

  if (!storedLanguage || !isSupportedLanguage(storedLanguage)) {
    const defaultLanguage = await getSystemLanguage();
    const language = defaultLanguage || fallbackLanguage;
    setItem('language', language);
    return language;
  }

  return storedLanguage;
};

export const isSupportedLanguage = (language: string | ELanguages): language is ELanguages => {
  return Object.values(ELanguages).includes(language as ELanguages);
};

// en-TT is no longer supported
const migrateSavedLegacyEnTTLanguage = () => {
  const language = getItem('language');
  if (language === 'en-TT') {
    setItem('language', ELanguages.en);
  }
};

initAppLanguage().then(language => {
  i18n.use(initReactI18next).init({
    lng: language,
    defaultNS: 'translations',
    fallbackLng: fallbackLanguage,
    resources: {},
    keySeparator: false, // we do not use keys in form messages.welcome
    interpolation: {
      escapeValue: false, // react already safes from xss
    },
  });
});

// there is an issue happening when we run the tests, this function depend on the env variables,
// for getting the translations links, and the env are not available when we run the tests
// even though we are mocking the env variables in the setupTests.js file but seems that this file is loading first
// before the mock took place/applied, so we are using a fallback url for the tests
const fallbackURL = 'https://translation-tool.cosafe.com/Cosafe/texts_export.php?lang=en';

const getTranslationUrl = (lang: ELanguages): string | undefined => {
  const envType = process.env.REACT_APP_ENV_NAME;

  let translationUrl: string;
  if (envType === EnvironmentEnum.PROD) {
    translationUrl = `https://static.cosafe.com/lang/app-texts-${lang}.json`;
  } else if (envType === EnvironmentEnum.STAGE) {
    translationUrl = `https://static.stage.cosafe.com/lang/app-texts-${lang}.json`;
  } else {
    translationUrl = `https://translation-tool.cosafe.com/Cosafe/texts_export.php?lang=${lang}`;
  }

  return translationUrl;
};

const getTranslation = async (lang: ELanguages) => {
  const translationsUrl = getTranslationUrl(lang) ?? fallbackURL;
  const res = await axios.get(translationsUrl ?? fallbackURL);
  return res.data;
};

export const addTranslations = async () => {
  try {
    const [
      enTranslations,
      seTranslations,
      nlTranslations,
      fiTranslations,
      ptTranslations,
      esTranslations,
    ] = await Promise.all([
      getTranslation(ELanguages.en),
      getTranslation(ELanguages.se),
      getTranslation(ELanguages.nl),
      getTranslation(ELanguages.fi),
      getTranslation(ELanguages.pt),
      getTranslation(ELanguages.es),
    ]);
    i18n.addResourceBundle(ELanguages.en, 'translations', enTranslations);
    i18n.addResourceBundle(ELanguages.se, 'translations', seTranslations);
    i18n.addResourceBundle(ELanguages.nl, 'translations', nlTranslations);
    i18n.addResourceBundle(ELanguages.fi, 'translations', fiTranslations);
    i18n.addResourceBundle(ELanguages.pt, 'translations', ptTranslations);
    i18n.addResourceBundle(ELanguages.es, 'translations', esTranslations);
  } catch (error) {
    console.error(error);
  }
  store.dispatch(setAreTranslationsLoading(false));
};

export const getResourceBundle = async (lang: ELanguages = ELanguages.en) => {
  const response = await i18n.getResourceBundle(lang, 'translations');
  return response;
};

export default i18n;
